/*
 * Shared filter panel with configurable fields and actions.
 * Supports plain text inputs, selects, searchable selects and
 * text+button combos for search operations.
 */

import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  Flex,
  FormControl,
  Input,
  Select,
  Button,
  Text,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, SearchIcon } from "@chakra-ui/icons";

const defaultFieldWidth = { base: "100%", md: "160px" };

const normalizeSearchText = (value = "") =>
  String(value)
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .trim();

const SearchableSelect = ({
  label,
  placeholder = "Seçiniz",
  options = [],
  value,
  onChange,
  isDisabled,
  fieldWidth = defaultFieldWidth,
}) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bg = useColorModeValue("white", "gray.700");

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const normalizedQuery = normalizeSearchText(query);
    return options.filter((opt) =>
      normalizeSearchText(opt.label).includes(normalizedQuery)
    );
  }, [options, query]);

  // Boş değer seçildiğinde placeholder göster
  const selectedLabel = value
    ? options.find((opt) => String(opt.value) === String(value))?.label || placeholder
    : placeholder;

  return (
    <FormControl w={fieldWidth} isDisabled={isDisabled}>
      <Popover
        placement="bottom-start"
        isLazy
        matchWidth
        isOpen={isOpen}
        initialFocusRef={inputRef}
        onClose={() => {
          setQuery("");
          onClose();
        }}
      >
        <PopoverTrigger>
          <Box
            as="button"
            type="button"
            w="100%"
            px={3}
            py={1.5}
            fontSize="sm"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
            color={value ? "gray.800" : "gray.500"}
            textAlign="left"
            cursor={isDisabled ? "not-allowed" : "pointer"}
            opacity={isDisabled ? 0.6 : 1}
            _hover={isDisabled ? {} : { borderColor: "blue.300" }}
            _focus={isDisabled ? {} : { borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
            disabled={isDisabled}
            onClick={isDisabled ? undefined : onOpen}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text isTruncated flex={1} mr={2}>
              {selectedLabel}
            </Text>
            <ChevronDownIcon />
          </Box>
        </PopoverTrigger>
        <PopoverContent w="240px" _focus={{ boxShadow: "lg" }}>
          <PopoverBody>
            <Input
              ref={inputRef}
              size="sm"
              placeholder="Ara..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              mb={2}
              autoFocus
            />
            <Box maxH="200px" overflowY="auto" bg={bg} borderRadius="md">
              {filteredOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant="ghost"
                  size="sm"
                  w="100%"
                  justifyContent="flex-start"
                  onClick={() => {
                    onChange(opt.value);
                    setQuery("");
                    onClose();
                  }}
                >
                  {opt.label}
                </Button>
              ))}
              {!filteredOptions.length && (
                <Box px={2} py={1} fontSize="sm" color="gray.500">
                  Sonuç bulunamadı
                </Box>
              )}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};

const FieldRenderer = ({ field, onKeyPress, fieldWidth = defaultFieldWidth }) => {
  const {
    type = "text",
    label,
    placeholder,
    value,
    onChange,
    options = [],
    isDisabled,
    buttonLabel,
    onButtonClick,
    inputType,
  } = field;

  if (type === "searchable-select") {
    return (
      <SearchableSelect
        label={label}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
        isDisabled={isDisabled}
        fieldWidth={fieldWidth}
      />
    );
  }

  if (type === "select") {
    return (
      <FormControl w={fieldWidth} isDisabled={isDisabled}>
        <Select
          size="sm"
          placeholder={placeholder || label}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          fontSize="sm"
          cursor="pointer"
          _hover={{ borderColor: "blue.300" }}
          w="100%"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (type === "text-button") {
    return (
      <FormControl w={fieldWidth} isDisabled={isDisabled}>
        <HStack spacing={2}>
          <Input
            size="sm"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            isDisabled={isDisabled}
          />
          <Button
            size="sm"
            colorScheme="blue"
            onClick={onButtonClick}
            isDisabled={isDisabled}
          >
            {buttonLabel || "Ara"}
          </Button>
        </HStack>
      </FormControl>
    );
  }

  return (
    <FormControl w={fieldWidth} isDisabled={isDisabled}>
      <Input
        type={inputType || "text"}
        size="sm"
        placeholder={placeholder || label}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === "Enter" && onKeyPress) {
            onKeyPress(e);
          }
        }}
        fontSize="sm"
        cursor="text"
        _hover={{ borderColor: "blue.300" }}
        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
        w="100%"
      />
    </FormControl>
  );
};

export default function FilterPanel({
  fields = [],
  actions = [],
  onReset,
  onSearch, // "Listele" butonu için callback
  searchButtonLabel = "Listele",
  searchButtonLoading = false,
  searchButtonLoadingText = "Yükleniyor...",
  onKeyPress, // Enter tuşu için callback
  fieldWidth, // Özel field genişliği (opsiyonel)
  buttonSize = "sm",
  gap = 3,
}) {
  const bg = useColorModeValue("white", "gray.800");

  return (
    <Box
      mt={8}
      mb={4}
      px={3}
      py={2.5}
      bg={bg}
      borderRadius="lg"
      boxShadow="sm"
      border="1px solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
      position="relative"
    >
      <Flex
        wrap="wrap"
        gap={gap}
        align="flex-end"
        justify="flex-start"
      >
        {fields.map((field) => (
          <FieldRenderer key={field.key} field={field} onKeyPress={onKeyPress} fieldWidth={fieldWidth} />
        ))}

        {onSearch && (
          <FormControl w={{ base: "100%", md: "auto" }}>
            <Button
              size={buttonSize}
              colorScheme="blue"
              leftIcon={<SearchIcon />}
              onClick={onSearch}
              isLoading={searchButtonLoading}
              loadingText={searchButtonLoadingText}
            >
              {searchButtonLabel}
            </Button>
          </FormControl>
        )}

        {onReset && (
          <FormControl w={{ base: "100%", md: "auto" }}>
            <Button
              size={buttonSize}
              variant="outline"
              colorScheme="gray"
              onClick={onReset}
            >
              Sıfırla
            </Button>
          </FormControl>
        )}

        {actions.map((action) => (
          <FormControl
            key={action.key || action.label}
            w={{ base: "100%", md: "auto" }}
          >
            <Button
              size={action.buttonProps?.size || buttonSize}
              {...action.buttonProps}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          </FormControl>
        ))}
      </Flex>
    </Box>
  );
}


